import { useState } from 'react';
import { MoreVertical, Palette, Tag, Unlink, Loader2, RefreshCw, KeyRound } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../../components/ui/dropdown-menu';
import { Button } from '../../../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { AccountCard, getInactiveReason } from '../../../entities/account';
import type { Account } from '../../../entities/account';
import { useAccountStore } from '../../../entities/account';
import { useLoadAccounts } from '../../../features/accounts-load';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../shared/config/routes';
import { AccountTagsDialog } from '../../../widgets/account-tags-dialog';
import { disconnectFacebook } from '../../../features/account-connect-facebook';
import { useRefreshAccount } from '../../../features/account-refresh';
import { UpdateTokenDialog, useReconnectInstagram } from '../../../features/account-update-token';
import { accountsApi } from '../../../shared/api/accounts.api';
import { toast } from 'sonner';
import { useCurrentUser } from '../../../shared/hooks/useCurrentUser';

interface AccountCardInteractiveProps {
  account: Account;
  /** Режим выбора для массовых тэгов. */
  selection?: { selected: boolean; onToggle: () => void };
  highlighted?: boolean;
}

/**
 * Widget: Интерактивная карточка Instagram-аккаунта
 * Композирует AccountCard (entity) + actions (features)
 */
export function AccountCardInteractive({ account, selection, highlighted }: AccountCardInteractiveProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tagsDialogOpen, setTagsDialogOpen] = useState(false);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const deleteAccount = useAccountStore((state) => state.deleteAccount);
  const { loadAccounts } = useLoadAccounts();
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh, isRefreshing } = useRefreshAccount();
  const { reconnect, loading: isReconnecting } = useReconnectInstagram(account.id, loadAccounts);
  const { isAdmin } = useCurrentUser();

  const handleDelete = async () => {
    setLoading(true);

    try {
      await accountsApi.delete(account.id);
      deleteAccount(account.id);
      toast.success(`Аккаунт @${account.username || account.name} успешно удален`);
      setDeleteDialogOpen(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Ошибка при удалении аккаунта';
      toast.error(errorMessage);
      console.error('Delete account error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnectFacebook = async () => {
    try {
      setIsDisconnecting(true);
      await disconnectFacebook(account.id);
      toast.success('Facebook отключен');
      loadAccounts();
      setDisconnectDialogOpen(false);
    } catch (error: any) {
      console.error('❌ Error disconnecting Facebook:', error);
      toast.error(error.message || 'Не удалось отключить Facebook');
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleRefresh = async () => {
    await refresh(account.id, account.username || account.name, loadAccounts);
  };

  const handleManageTemplate = () => {
    // Редактор account-centric: грузит оба шаблона (reel + post) по accountId.
    // from — куда вернуться после «Сохранить».
    navigate(`${ROUTES.TEMPLATE_NEW}?accountId=${account.id}`, {
      state: { from: location.pathname + location.search },
    });
  };

  // Отвязка осталась только у старых записей с привязанной страницей:
  // подключать Facebook больше не нужно (решение владельца, 02.11.2026).
  const hasFacebook = !!account.facebookPageId;

  // Клик по карточке — статистика аккаунта; профиль в Instagram — иконкой.
  const handleCardClick = () => navigate(`/analytics/account/${encodeURIComponent(account.id)}`);
  const externalUrl = account.username ? `https://www.instagram.com/${account.username}/` : undefined;

  // Протухший или выключенный аккаунт: действие — прямо на карточке, а не в меню.
  const isInactive = getInactiveReason(account) !== null;
  const footer = !isInactive ? undefined : isAdmin ? (
    <Button size="sm" variant="outline" className="w-full" onClick={() => void reconnect()} disabled={isReconnecting}>
      {isReconnecting ? <Loader2 className="animate-spin" /> : <KeyRound />}
      Переподключить
    </Button>
  ) : (
    <p className="text-xs text-muted-foreground">Попросите администратора переподключить</p>
  );

  // Dropdown Menu Actions
  const actionsMenu = (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Действия с аккаунтом">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleManageTemplate}>
            <Palette className="h-4 w-4" />
            Оформление
          </DropdownMenuItem>

          <DropdownMenuItem onClick={handleRefresh} disabled={isRefreshing}>
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Обновление...' : 'Обновить данные'}
          </DropdownMenuItem>

          {isAdmin && <UpdateTokenDialog accountId={account.id} onSuccess={loadAccounts} />}

          {hasFacebook && (
            <DropdownMenuItem onClick={() => setDisconnectDialogOpen(true)}>
              <Unlink className="h-4 w-4" />
              Отключить Facebook
            </DropdownMenuItem>
          )}

          <DropdownMenuItem onClick={() => setTagsDialogOpen(true)}>
            <Tag className="h-4 w-4" />
            Управление тэгами
          </DropdownMenuItem>
          {isAdmin && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive"
                onClick={() => setDeleteDialogOpen(true)}
              >
                Удалить аккаунт
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Диалог управления тэгами */}
      <AccountTagsDialog
        account={account}
        open={tagsDialogOpen}
        onOpenChange={setTagsDialogOpen}
      />

      {/* Диалог удаления */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить аккаунт?</AlertDialogTitle>
            <AlertDialogDescription>
              Вы действительно хотите удалить аккаунт <strong>@{account.username || account.name}</strong>?
              <br />
              Это действие нельзя отменить. Все связанные посты и шаблоны останутся, но вы не сможете публиковать контент.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading}>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={loading} className="bg-destructive hover:bg-destructive/90">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Диалог отключения Facebook */}
      <AlertDialog open={disconnectDialogOpen} onOpenChange={setDisconnectDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Отключить Facebook?</AlertDialogTitle>
            <AlertDialogDescription>
              Вы уверены, что хотите отключить Facebook от этого аккаунта?
              Вы больше не сможете публиковать через Facebook API.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDisconnecting}>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={handleDisconnectFacebook} disabled={isDisconnecting}>
              {isDisconnecting && <Loader2 className="h-4 w-4 animate-spin" />}
              Отключить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </>
  );

  return (
    <AccountCard
      account={account}
      actions={actionsMenu}
      onClick={handleCardClick}
      externalUrl={externalUrl}
      selection={selection}
      footer={footer}
      highlighted={highlighted}
    />
  );
}
